import React, { useState, useMemo, useRef } from 'react';

import { FormDialog, FormProps } from '@components/admin/FormDialog';
import { TextInput } from '@components/admin/Forms';
import { EmailDuplicateDialog } from '@components/admin/members/EmailDuplicateDialog';
import { MembershipTextInput } from '@components/admin/members/MembershipFormDetails/Forms';
import { Alert } from '@components/Alert';
import { Button } from '@components/Button';
import { FormTitle } from '@components/Forms/FormTitle';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { Layout } from '@components/Layout';
import { Pagination } from '@components/Pagination';
import { QueryContainer } from '@components/QueryContainer';
import { EmailChangePromptDialog } from '@components/settings/EmailChangePromptDialog';
import { MembershipContainer } from '@components/settings/MembershipContainer';
import { SpinnerModal } from '@components/SpinnerModal';
import { Table } from '@components/Table/Table';
import {
  MembershipNameTextDisplay,
  MembershipTextDisplay,
} from '@components/utilities/MembershipForm/Form';
import { strings as membershipFormStrings } from '@data/membershipForm';
import { userStrings } from '@data/user';
import { useCompanyStaffCreate } from '@hooks/mutation/useCompanyStaffCreate';
import { useCompanyStaffDelete } from '@hooks/mutation/useCompanyStaffDelete';
import { usePatchUser } from '@hooks/mutation/usePatchUser';
import { useCompany } from '@hooks/queries/useCompany';
import { useCompanyStaffs } from '@hooks/queries/useCompanyStaffs';
import { useUser } from '@hooks/queries/useUser';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useSearch } from '@hooks/useSearch';
import { MembershipFormFields } from '@models/MembershipForm';
import { User } from '@models/User';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { EmailDuplicateError, FormError } from '@utils/errors';
import {
  createValidate,
  formStrings,
  newEmailField,
  requiredStringField,
  stringField,
} from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik, FormikProps } from 'formik';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: membershipFormStrings.principalInfo,
    staffManagement: 'スタッフ管理',
    staffManagementHint:
      '複数のスタッフを追加できます。スタッフは会社名義でのブッキングやブッキング管理などができます。',
    addStaff: 'スタッフを追加',
    no: 'No',
    name: '名前',
    cellphone: '携帯電話番号',
    landline: '固定電話番号',
    email: 'メール',
    action: '操作',
    delete: '削除',
    editStaff: 'スタッフ情報を変更する',
    lastName: '姓',
    firstName: '名',
    confirmDeleteStaff: (name: string) => `本当に「${name}」さんをスタッフから削除しますか？`,
    updateAssigneeInfo: '担当者情報を変更する',
    updateStaffInfo: 'スタッフ情報を変更する',
    confirmChangeStaffInfo: 'スタッフ情報を変更しますか？',
    confirmChangeEmail: (email: string) => (
      <>
        メールアドレスを<span style={{ color: '#CF3F1B' }}>{email}</span>に変更されますか？
      </>
    ),
    afterChangeEmail:
      '「OK」をクリックすると現在のメールアドレスではD2Dにログインできなくなりますので、ご注意ください。',
  },
});

const useStyles = makeMuiStyles((_, common) => ({
  container: {
    ...common.flexColumn,
    padding: '1rem 2rem',
  },
  contactToChangeInformation: {
    ...common.middleText,
    ...common.textError,
    padding: '1rem',
  },
  hint: {
    ...common.smallerText,
    ...common.textGray500,
  },
  formLabel: {
    width: 130,
  },
  tableLoadingContainer: {
    height: 200,
  },
  disabledEdit: {
    cursor: 'not-allowed',
  },
  principalForm: {
    ...common.verticalSpace1,
    paddingLeft: '0.5rem',
  },
}));

const query = t.type({
  staffPage: h.omittable(h.number().cast()),
});

interface EditUserProfileFormProps extends FormProps {
  user: User;
}

const EditUserProfileForm: React.FC<EditUserProfileFormProps> = (props) => {
  const { user, formikRef, steps } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const initialValues = useMemo(
    () => ({
      last_name: user.last_name,
      first_name: user.first_name,
      email: user.email,
      cellphone: user.cellphone,
      landline: user.landline,
    }),
    [],
  );

  const schema = useMemo(
    () =>
      t.type({
        last_name: requiredStringField(strings.lastName),
        first_name: requiredStringField(strings.firstName),
        email: requiredStringField(strings.email),
        cellphone: stringField(strings.cellphone),
        landline: stringField(strings.landline),
      }),
    [],
  );

  const validate = useMemo(() => createValidate(schema), [schema]);

  const patchUser = usePatchUser();

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      innerRef={formikRef}
      onSubmit={async (values) => {
        await patchUser.mutateAsync(values);
      }}
    >
      {({ values }) => {
        const emailChanged = values.email !== user.email;
        return (
          <Form className={clsx(classes.verticalSpace1)}>
            <Alert severity="error" open={Boolean(patchUser.error)}>
              {patchUser.error instanceof EmailDuplicateError
                ? userStrings.emailDuplicate
                : patchUser.error instanceof FormError
                ? patchUser.error.getDisplayMessage()
                : (patchUser.error as Error)?.message ?? formStrings.unknownError}
            </Alert>
            <SubmittingOverlay />

            {steps?.currentStep == 0 && (
              <>
                <TextInput
                  label={
                    <>
                      {strings.lastName}
                      <span className={classes.textError}>{formStrings.required}</span>
                    </>
                  }
                  name="last_name"
                />
                <TextInput
                  label={
                    <>
                      {strings.firstName}
                      <span className={classes.textError}>{formStrings.required}</span>
                    </>
                  }
                  name="first_name"
                />
                <TextInput
                  label={
                    <>
                      {strings.email}
                      <span className={classes.textError}>{formStrings.required}</span>
                    </>
                  }
                  name="email"
                />
                <TextInput label={strings.cellphone} name="cellphone" />
                <TextInput label={strings.landline} name="landline" />
              </>
            )}
            {steps?.currentStep === 1 && emailChanged && (
              <>
                <div className={classes.fontWeight700}>
                  {strings.confirmChangeEmail(values.email)}
                </div>
                <div className={classes.smallerText}>{strings.afterChangeEmail}</div>
              </>
            )}
            {steps?.currentStep === 1 && !emailChanged && (
              <div className={clsx(classes.fontWeight700, classes.textAlignCenter)}>
                {strings.confirmChangeStaffInfo}
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

const AddEmployeeForm: React.FC<FormProps> = (props) => {
  const { formikRef } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const initialValues = useMemo(
    () => ({
      last_name: '',
      first_name: '',
      email: '',
    }),
    [],
  );

  const schema = useMemo(
    () =>
      t.type({
        last_name: requiredStringField(strings.lastName),
        first_name: requiredStringField(strings.firstName),
        email: newEmailField(strings.email),
      }),
    [],
  );

  const validate = useMemo(() => createValidate(schema), [schema]);

  const { mutateAsync, error } = useCompanyStaffCreate();

  return (
    <Formik
      initialValues={initialValues}
      validate={validate}
      innerRef={formikRef}
      onSubmit={async (values) => {
        await mutateAsync(values);
      }}
    >
      <Form className={classes.verticalSpace1}>
        <Alert severity="error" open={Boolean(error)}>
          {error instanceof FormError
            ? error.getDisplayMessage()
            : (error as Error)?.message ?? formStrings.unknownError}
        </Alert>

        <SubmittingOverlay />
        <TextInput
          formClasses={{
            label: classes.formLabel,
          }}
          label={
            <>
              {strings.lastName}
              <span className={classes.textError}>{formStrings.required}</span>
            </>
          }
          name="last_name"
        />
        <TextInput
          formClasses={{
            label: classes.formLabel,
          }}
          label={
            <>
              {strings.firstName}
              <span className={classes.textError}>{formStrings.required}</span>
            </>
          }
          name="first_name"
        />
        <TextInput
          formClasses={{
            label: classes.formLabel,
          }}
          label={
            <>
              {strings.email}
              <span className={classes.textError}>{formStrings.required}</span>
            </>
          }
          name="email"
        />
      </Form>
    </Formik>
  );
};

const STAFF_PAGE_SIZE = 10;

interface EmployeeTableProps {
  isPrincipal: boolean;
}

const EmployeeTable: React.FC<EmployeeTableProps> = (props) => {
  const { isPrincipal } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const user = useUser();
  const { search, updateSearch } = useSearch(query);
  const [openEditStaff, setOpenEditStaff] = useState(false);

  const { staffPage = 0 } = search;

  const companyStaffs = useCompanyStaffs(staffPage!);
  const companyStaffDelete = useCompanyStaffDelete();

  const { renderDialog, confirm } = useConfirmDialog();

  const headColumns = useMemo(
    () => [
      {
        label: strings.no,
      },
      {
        label: strings.name,
      },
      {
        label: strings.cellphone,
      },
      {
        label: strings.landline,
      },
      {
        label: strings.email,
      },
      {
        label: strings.action,
      },
    ],
    [isPrincipal],
  );

  const onDeleteStaff = async (id: number, name: string) => {
    const result = await confirm({
      message: (
        <>
          <br />
          <br />
          {strings.confirmDeleteStaff(name)}
          <br />
          <br />
          <br />
        </>
      ),
    });

    switch (result) {
      case 'cancelled':
        break;
      case 'confirmed':
        await companyStaffDelete.mutateAsync(id);
        break;
      default:
    }
  };

  const onEditStaff = () => {
    setOpenEditStaff(true);
  };

  const renderTable = () => (
    <Table>
      <Table.Head>
        <Table.Row columns={headColumns} />
      </Table.Head>

      {companyStaffs.data?.results.map((staff, i) => (
        <Table.Row
          columns={[
            {
              label: STAFF_PAGE_SIZE * staffPage! + i + 1,
            },
            {
              label: `${staff.last_name} ${staff.first_name}`,
            },
            {
              label: staff.cellphone,
            },
            {
              label: staff.landline,
            },
            {
              label: staff.email || '-',
            },
            ...(isPrincipal
              ? [
                  {
                    label: (
                      <span
                        onClick={() =>
                          onDeleteStaff(staff.id, `${staff.last_name} ${staff.first_name}`)
                        }
                        className={clsx(classes.textError, classes.baseButton)}
                      >
                        {strings.delete}
                      </span>
                    ),
                  },
                ]
              : [
                  {
                    label:
                      staff.id === user.data?.id ? (
                        <span
                          onClick={onEditStaff}
                          className={clsx([classes.link, classes.baseButton])}
                        >
                          {formStrings.edit}
                        </span>
                      ) : (
                        '-'
                      ),
                  },
                ]),
          ]}
        />
      ))}
    </Table>
  );

  return (
    <>
      {renderDialog()}
      <FormDialog
        title={strings.editStaff}
        open={openEditStaff}
        onClose={() => setOpenEditStaff(false)}
        dialogProps={{
          maxWidth: 'sm',
          fullWidth: true,
        }}
        maxSteps={1}
      >
        <EditUserProfileForm user={user.data!} />
      </FormDialog>

      <SpinnerModal open={companyStaffDelete.isLoading} />
      <QueryContainer className={classes.tableLoadingContainer} queries={[companyStaffs]}>
        {renderTable()}
      </QueryContainer>
      <div style={{ height: '1rem' }} />
      {companyStaffs.isSuccess && (
        <div className={clsx(classes.flexInline, classes.justifyEnd)}>
          <Pagination
            page={staffPage!}
            count={Math.ceil(companyStaffs.data!.count! / STAFF_PAGE_SIZE)}
            // eslint-disable-next-line @typescript-eslint/no-shadow
            onChange={(staffPage) => updateSearch({ staffPage })}
          />
        </div>
      )}
    </>
  );
};

export const PrincipalInfo: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [isEditingPrincipal, setIsEditingPrincipal] = useState(false);
  const [openAddStaff, setOpenAddStaff] = useState(false);
  const [openEmailChangePromptDialog, setOpenEmailChangePromptDialog] = useState(false);
  const [openEmailDuplicateDialog, setOpenEmailDuplicateDialog] = useState(false);

  const user = useUser();
  const patchUser = usePatchUser();
  const { data, isLoading } = useCompany();

  const schema = useMemo(() => MembershipFormFields.shared.toPrincipalFormSchema(), []);

  const validate = useMemo(() => createValidate(schema), [schema]);

  const initialValues = useMemo(
    () => user.data && MembershipFormFields.shared.mapCompanyPrincipalToPrincipalForm(user.data),
    [user.data],
  );

  const principalFormikRef =
    useRef<FormikProps<Exclude<typeof initialValues, undefined | null>>>(null);

  const onClickCancelEditingPrincipal = () => {
    setIsEditingPrincipal(false);
  };

  const onClickTitleButton = async () => {
    if (!isEditingPrincipal) {
      setIsEditingPrincipal(true);
    } else if (principalFormikRef.current?.isValid) {
      await principalFormikRef.current?.submitForm();
    }
  };

  const onClickAddStaff = async () => {
    setOpenAddStaff(true);
  };

  const renderPrincipalDisplay = () => (
    <>
      <MembershipNameTextDisplay
        lastNameField={MembershipFormFields.shared.principalLastName}
        lastName={data!.user.last_name}
        firstNameField={MembershipFormFields.shared.principalFirstName}
        firstName={data!.user.first_name}
      />
      <MembershipTextDisplay
        field={MembershipFormFields.shared.principalCellphone}
        value={data!.user.cellphone}
      />
      <MembershipTextDisplay
        field={MembershipFormFields.shared.principalLandline}
        value={data!.user.landline}
      />
      <MembershipTextDisplay
        field={MembershipFormFields.shared.principalEmail}
        value={<span className={classes.whitespacePre}>{data!.user.email || '-'}</span>}
      />
    </>
  );

  const renderPrincipalForm = () => (
    <Formik
      validate={validate}
      innerRef={principalFormikRef}
      initialValues={initialValues!}
      onSubmit={async (values) => {
        if (values.principalEmail === initialValues?.principalEmail) {
          await patchUser.mutateAsync(
            MembershipFormFields.shared.mapPrincipalFormToPatchPrincipal(values),
          );
          setIsEditingPrincipal(false);
        } else {
          setOpenEmailChangePromptDialog(true);
        }
      }}
    >
      {({ values }) => (
        <Form className={classes.principalForm}>
          <SubmittingOverlay />
          <EmailChangePromptDialog
            title={strings.updateAssigneeInfo}
            email={values.principalEmail}
            open={openEmailChangePromptDialog}
            onClose={() => setOpenEmailChangePromptDialog(false)}
            onConfirm={async () => {
              try {
                await patchUser.mutateAsync(
                  MembershipFormFields.shared.mapPrincipalFormToPatchPrincipal(values),
                );
                setOpenEmailChangePromptDialog(false);
                setIsEditingPrincipal(false);
              } catch (e) {
                if (e instanceof EmailDuplicateError) {
                  setOpenEmailChangePromptDialog(false);
                  setOpenEmailDuplicateDialog(true);
                } else if (e instanceof FormError) {
                  // eslint-disable-next-line no-alert
                  alert(e.getDisplayMessage());
                } else {
                  alert(String(e));
                }
                throw e;
              }
            }}
          />
          <EmailDuplicateDialog
            open={openEmailDuplicateDialog}
            onClose={() => setOpenEmailDuplicateDialog(false)}
          />
          <MembershipTextInput required field={MembershipFormFields.shared.principalLastName} />
          <MembershipTextInput required field={MembershipFormFields.shared.principalFirstName} />
          <MembershipTextInput required field={MembershipFormFields.shared.principalCellphone} />
          <MembershipTextInput field={MembershipFormFields.shared.principalLandline} />
          <MembershipTextInput required field={MembershipFormFields.shared.principalEmail} />
        </Form>
      )}
    </Formik>
  );

  return (
    <Layout
      loading={isLoading}
      title={strings.title}
      breadcrumbs={['/utilities/Dashboard', '/settings/Membership/PrincipalInfo']}
    >
      {() => (
        <MembershipContainer>
          <div className={classes.container}>
            <FormTitle
              title={membershipFormStrings.principalInfo}
              actions={
                <>
                  {user.data?.is_principal && isEditingPrincipal && (
                    <Button
                      text={formStrings.cancel}
                      color="default"
                      size="small"
                      onClick={onClickCancelEditingPrincipal}
                    />
                  )}
                  {user.data?.is_principal && (
                    <Button
                      text={isEditingPrincipal ? formStrings.update : formStrings.edit}
                      color="primary"
                      size="small"
                      onClick={onClickTitleButton}
                    />
                  )}
                </>
              }
            />
            {!isEditingPrincipal && renderPrincipalDisplay()}
            {isEditingPrincipal && renderPrincipalForm()}

            <div style={{ height: '2rem' }} />

            <FormTitle
              title={strings.staffManagement}
              actions={
                user.data?.is_principal && (
                  <Button
                    text={strings.addStaff}
                    color="primary"
                    size="small"
                    onClick={onClickAddStaff}
                  />
                )
              }
            />

            <div className={classes.hint}>{strings.staffManagementHint}</div>

            <div style={{ height: '1rem' }} />

            <FormDialog
              title={strings.addStaff}
              open={openAddStaff}
              onClose={() => setOpenAddStaff(false)}
            >
              <AddEmployeeForm />
            </FormDialog>

            <EmployeeTable isPrincipal={user.data?.is_principal ?? false} />
          </div>
        </MembershipContainer>
      )}
    </Layout>
  );
};
